<template>
  <LayoutGame>
    <div :key="render" class="game">
      <!-- Home Screen -->
      <div v-if="homeScreenStatus" class="home-screen">
        <Home />
      </div>

      <!-- Main Game Screen -->
      <div v-show="!homeScreenStatus && !gameCompleted" :key="render" class="game game-start">
        <!-- Banners -->
        <h1 v-if="bannerQuestionStatus" class="banner banner-orange">{{ newQuestions[currentExercise].question }}</h1>
        <img
          v-if="bannerCorrectStatus"
          class="banner banner-correct"
          src="@/assets/practices/easter_egg/banner_correct.svg"
          alt="banner_correct"
        />
        <img
          v-if="bannerIncorrectStatus"
          class="banner banner-incorrect"
          src="@/assets/practices/easter_egg/banner_incorrect.svg"
          alt="banner_incorrect"
        />

        <!-- Explanation Screen -->

        <div v-if="explanationMessageStatus" class="explanation">
          <p>{{ newQuestions[currentExercise].explanation }}</p>
          <button class="continue-button" @click="closeExplanation">Continue</button>
        </div>

        <!-- Egg Grid -->
        <div class="grid">
          <div v-for="{ id, name, path, classColumn } in gridLayout" :key="id" :class="classColumn">
            <Egg
              :egg-number="newQuestions[currentExercise].answerOptions[id].number"
              :egg-name="name"
              :egg-path="path"
              :egg-answer="newQuestions[currentExercise].answerOptions[id].answer"
              :bunny-id="'bunny' + id"
              :egg-id="'egg' + id"
            />
          </div>
        </div>

        <!-- Points -->
        <div class="points-div">
          <div class="points">Points: {{ playerScore }}</div>
          <img class="points-end" src="@/assets/practices/easter_egg/banner_tail.svg" alt="banner_tail" />
        </div>

        <!-- Lives -->
        <div class="lives">
          <ul>
            <li v-for="index in currentLives" :key="index">
              <img class="life" :src="require(`@/assets/practices/easter_egg/${life.activeLife}`)" alt="life" />
            </li>
            <li v-for="index in maxLives - currentLives" :key="index + 10">
              <img class="life" :src="require(`@/assets/practices/easter_egg/${life.inactiveLife}`)" alt="life" />
            </li>
          </ul>
        </div>
      </div>

      <!-- Next Exercise Screen -->
      <div v-show="exerciseCompleted && currentLives >= 1 && currentExercise < 4" class="next-exercise-screen">
        <button class="next-exercise-button" @click="nextExercise">Continue</button>
      </div>

      <!-- Game End Screen -->
      <div v-show="gameCompleted" class="game game-end">
        <!-- Send user to Score.vue view -->
        <div v-if="currentLives >= 1" class="score">
          <Score :player-game-won="true" />
        </div>
        <div v-if="currentLives <= 0" class="score">
          <Score :player-game-won="false" />
        </div>
      </div>

      <Settings />

      <!-- Background -->
      <div class="background">
        <img id="cloud-fg-one" class="clouds-foreground" src="@/assets/practices/easter_egg/cloud.svg" alt="cloud" />
        <img
          id="cloud-fg-one-loop"
          class="clouds-foreground"
          src="@/assets/practices/easter_egg/cloud.svg"
          alt="cloud"
        />
        <img id="cloud-fg-two" class="clouds-foreground" src="@/assets/practices/easter_egg/cloud.svg" alt="cloud" />
        <img
          id="cloud-fg-two-loop"
          class="clouds-foreground"
          src="@/assets/practices/easter_egg/cloud.svg"
          alt="cloud"
        />
        <img id="cloud-mg-one" class="clouds-middleground" src="@/assets/practices/easter_egg/cloud.svg" alt="cloud" />
        <img
          id="cloud-mg-one-loop"
          class="clouds-middleground"
          src="@/assets/practices/easter_egg/cloud.svg"
          alt="cloud"
        />
        <img id="cloud-mg-two" class="clouds-middleground" src="@/assets/practices/easter_egg/cloud.svg" alt="cloud" />
        <img
          id="cloud-mg-two-loop"
          class="clouds-middleground"
          src="@/assets/practices/easter_egg/cloud.svg"
          alt="cloud"
        />
        <img id="cloud-bg-one" class="clouds-background" src="@/assets/practices/easter_egg/cloud.svg" alt="cloud" />
        <img
          id="cloud-bg-one-loop"
          class="clouds-background"
          src="@/assets/practices/easter_egg/cloud.svg"
          alt="cloud"
        />
        <img id="cloud-bg-two" class="clouds-background" src="@/assets/practices/easter_egg/cloud.svg" alt="cloud" />
        <img
          id="cloud-bg-two-loop"
          class="clouds-background"
          src="@/assets/practices/easter_egg/cloud.svg"
          alt="cloud"
        />
        <img id="flower-bg-one" class="flower" src="@/assets/practices/easter_egg/flower.svg" alt="flower" />
        <!-- <img class="flower" id="flower-bg-two" src="@/assets/practices/easter_egg/flower.svg" alt="flower"> -->
        <img id="flower-bg-three" class="flower" src="@/assets/practices/easter_egg/flower.svg" alt="flower" />
      </div></div
  ></LayoutGame>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
// import { setTimeout } from 'timers';
// import { Howl } from 'howler';
import Egg from '@/components/practices/easter_egg/Egg.vue';
import Score from '@/components/practices/easter_egg/Score.vue';
import questions from '@/assets/practices/data/easter_egg/questions.json';
import Home from '@/components/practices/easter_egg/Home.vue';
import Settings from '@/components/practices/Settings.vue';
import sounds from '@/packages/sounds/index.js';

export default {
  name: 'Game',
  components: {
    Egg,
    Score,
    Home,
    Settings,
    LayoutGame: () => import('@/layouts/LayoutGame.vue'),
  },
  data: () => ({
    questions,
    newQuestions: [],
    banner: {
      animationPlayState: '',
    },
    life: {
      activeLife: 'life.svg',
      inactiveLife: 'life_dim.svg',
    },
    gridLayout: [
      {
        id: 0,
        name: 'egg_stars',
        path: 'egg_stars.svg',
        classColumn: 'egg column1',
      },
      {
        id: 1,
        name: 'egg_hearts',
        path: 'egg_hearts.svg',
        classColumn: 'egg column2',
      },
      {
        id: 2,
        name: 'egg_plant',
        path: 'egg_plant.svg',
        classColumn: 'egg column3',
      },
      {
        id: 3,
        name: 'egg_clouds',
        path: 'egg_clouds.svg',
        classColumn: 'egg column4',
      },
    ],
    render: 0,
  }),
  computed: {
    ...mapGetters({
      bannerCorrectStatus: 'easterEgg/bannerCorrectStatus',
      bannerIncorrectStatus: 'easterEgg/bannerIncorrectStatus',
      bannerQuestionStatus: 'easterEgg/bannerQuestionStatus',
      currentExercise: 'easterEgg/currentExercise',
      currentLives: 'easterEgg/currentLives',
      exerciseCompleted: 'easterEgg/exerciseCompleted',
      explanationMessageStatus: 'easterEgg/explanationMessageStatus',
      gameCompleted: 'easterEgg/gameCompleted',
      homeScreenStatus: 'easterEgg/homeScreenStatus',
      maxLives: 'easterEgg/maxLives',
      muteStatus: 'settings/muteStatus',
      playerScore: 'easterEgg/playerScore',
    }),
  },
  watch: {
    muteStatus: state => {
      if (state) {
        sounds.mute(true);
      } else {
        sounds.mute(false);
      }
    },
  },
  created() {
    this.newQuestions = this.getRandom(questions, 5);
  },
  mounted() {
    sounds.easterEggBackground.play();
  },
  methods: {
    ...mapActions({ increment: 'easterEgg/increment' }),
    nextExercise() {
      sounds.globalButton.play();
      this.$store.state.easterEgg.exerciseCompleted = false;
      this.$store.state.easterEgg.exercisePrevent = false;
      this.increment();
      this.$store.state.easterEgg.bannerQuestionStatus = true;
      this.checkGameOver();
      this.render += 1;
      this.$confetti.stop();
    },
    closeExplanation() {
      sounds.globalButton.play();
      this.$confetti.stop();
      this.$store.state.easterEgg.explanationMessageStatus = false;
      // Click "Continue" to move to the next exercise.
      this.$store.state.easterEgg.exerciseCompleted = true;
      this.checkGameOver();
    },
    checkGameOver() {
      if (
        (this.$store.state.easterEgg.exerciseCompleted && this.$store.state.easterEgg.currentLives <= 0) ||
        (this.$store.state.easterEgg.exerciseCompleted && this.$store.state.easterEgg.currentExercise >= 4)
      ) {
        this.$store.state.easterEgg.gameCompleted = true;
        this.$confetti.stop();
      }
    },
    getRandom(arr, n) {
      const result = new Array(n);
      let len = arr.length;
      const taken = new Array(len);
      if (n > len) throw new RangeError('getRandom: more elements taken than available');
      while (n--) {
        const x = Math.floor(Math.random() * len);
        result[n] = arr[x in taken ? taken[x] : x];
        taken[x] = --len in taken ? taken[len] : len;
      }
      return result;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/main.scss';

@font-face {
  font-family: 'VanillaExtractRegular';
  src: url('~@/assets/fonts/VanillaExtractRegular.eot');
  src: url('~@/assets/fonts/VanillaExtractRegular.eot?#iefix') format('embedded-opentype'),
    url('~@/assets/fonts/VanillaExtractRegular.svg#VanillaExtractRegular') format('svg'),
    url('~@/assets/fonts/VanillaExtractRegular.ttf') format('truetype'),
    url('~@/assets/fonts/VanillaExtractRegular.woff') format('woff');
  font-weight: normal;
  font-style: normal;
}
* {
  font-family: 'VanillaExtractRegular';
}

@mixin flex {
  display: flex;
  justify-content: center;
}

button {
  background-color: $easter-egg-button-main-color;
  color: #fff;
  padding: 10px 20px;
  border: none;
  border-radius: 999px;
  box-shadow: 5px 10px 0px 0px $easter-egg-button-shadow-color;
  cursor: pointer;
  text-align: center;
}

.game {
  @include flex;
  height: 100vh;
  align-items: center;

  .banner {
    position: absolute;
    padding: 1em 1em 1.25em 2.75em;
    @include flex;
    justify-content: center;
    align-items: center;
    background-repeat: no-repeat;
    background-position: center center;
    color: white;
    font-size: 1.25em;
    top: 15vh;
  }

  .banner-orange {
    background-image: url(~@/assets/practices/easter_egg/banner_orange.svg);
    min-width: 550px;
    // max-width: 90vw;
    // Animation
    animation-name: fly-half;
    animation-duration: 2s;
    animation-fill-mode: forwards;
  }

  .banner-correct,
  .banner-incorrect {
    padding: 1em 1.25em 1.25em 2em;
    height: 10vw;
    // Animation
    animation-name: fly-full;
    animation-duration: 6s;
    animation-fill-mode: forwards;
  }

  .banner-orange::after {
    background-image: url(~@/assets/practices/easter_egg/plane_orange.svg);
    top: 10%;
    left: 55%;
    content: ' ';
    background-repeat: no-repeat;
    background-position: center center;
    // display: block;
    position: absolute;
    height: 100%;
    width: 100%;
  }

  .grid {
    display: grid;
    grid-template: 'column1 column2 column3 column4';
    grid-template-columns: min-content min-content min-content min-content;
    grid-gap: 7.5vw;
    justify-content: center;
    align-items: center;
    transform: translateY(15vh);

    .column1 {
      grid-area: column1;
      // min-width: 200px;
    }
    .column2 {
      grid-area: column2;
      // min-width: 200px;
    }
    .column3 {
      grid-area: column3;
      // min-width: 200px;
    }
    .column4 {
      grid-area: column4;
      // min-width: 200px;
    }

    .egg {
      @include flex;
      align-items: flex-end;
      position: relative;
    }
  }

  .points-div {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    left: 0;
    top: 0;
  }

  .points {
    display: flex;
    justify-content: center;
    align-items: center;
    // background-image: url(~@/assets/practices/easter_egg/banner_points.svg);
    // background-repeat: no-repeat;
    // background-position: center center;
    // background-size: 100%;
    background-color: #4e30ec;
    height: 50px;
    width: 200px;
    color: white;
    // height: 0.25vw;
    font-size: 30px;
    padding: 0 0.75em;
    // z-index: -1;
  }

  .points-end {
    height: 50px;
    margin-left: -24px;
    // z-index: -1;
  }

  .lives {
    grid-area: lives;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    right: 0;
    top: 0;
    background-image: url(~@/assets/practices/easter_egg/banner_blue.svg);
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 100%;
    color: white;
    height: 50px;
    width: 170px;
    // z-index: -1;

    ul {
      @include flex;
      list-style: none;

      li {
        margin-left: 10px;

        .life {
          height: 35px;
        }
      }
    }
  }

  .explanation {
    @include flex;
    flex-direction: column;
    position: absolute;
    align-items: center;
    background-color: #362b2bcc;
    color: white;
    font-size: 1.5em;
    width: 100%;
    height: 100%;
    z-index: 2;

    p {
      text-align: center;
      padding: 1.5em;
    }
  }

  .next-exercise-screen {
    position: absolute;
    height: 100vh;
    // width: 100vw;
    @include flex;
    align-items: center;
    font-size: 1.75em;
    z-index: 1;

    .next-exercise-button {
      transform: translateY(-20vh);
    }
  }

  .game-end {
    font-size: 1.25em;
    // z-index: 2;
  }

  .background {
    background-image: url('~@/assets/practices/easter_egg/background_field.svg');
    background-repeat: no-repeat;
    background-size: cover;
    background-position-y: center;
    height: 100vh;
    width: 100vw;
    position: absolute;
    display: flex;
    justify-content: center;
    z-index: -2;

    // Temporary fix to overflow after banner (Game.vue) animation.
    // overflow: hidden;

    .flower {
      position: absolute;
      bottom: 0;
    }

    #flower-bg-one {
      height: 13vmin;
      transform: translate(-22vw, -2.5vh);
    }
    #flower-bg-two {
      height: 11vmin;
      transform: translate(45vw, -25vh);
    }
    #flower-bg-three {
      height: 15vmin;
      transform: translate(22vw, -2vh);
    }

    .clouds-background {
      position: absolute;
      height: 6.5vh;
      animation: clouds-move 60s infinite linear;
      z-index: -1;
    }

    .clouds-middleground {
      position: absolute;
      height: 10vh;
      animation: clouds-move 30s infinite linear;
      z-index: -1;
    }

    .clouds-foreground {
      position: absolute;
      height: 13vh;
      animation: clouds-move 15s infinite linear;
      z-index: -1;
    }

    #cloud-mg-one {
      top: 0vh;
      left: 40vw;
    }

    #cloud-mg-one-loop {
      top: 0vh;
      left: -80vw;
    }

    #cloud-mg-two {
      top: 10vh;
      left: 0vw;
    }

    #cloud-mg-two-loop {
      top: 10vh;
      left: -120vw;
    }

    #cloud-fg-one {
      top: 25vh;
      left: -140vw;
    }

    #cloud-fg-one-loop {
      top: 25vh;
      left: -20vw;
    }

    #cloud-fg-two {
      top: 20vh;
      left: 40vw;
    }

    #cloud-fg-two-loop {
      top: 20vh;
      left: -80vw;
    }

    #cloud-bg-one {
      top: 15vh;
      left: 70vw;
    }

    #cloud-bg-one-loop {
      top: 15vh;
      left: -50vw;
    }

    #cloud-bg-two {
      top: 5vh;
      left: 10vw;
    }

    #cloud-bg-two-loop {
      top: 5vh;
      left: -110vw;
    }

    @keyframes clouds-move {
      0% {
        transform: translateX(0vw);
      }
      100% {
        transform: translateX(120vw);
      }
    }
  }

  // .mute {
  //   @include flex;
  //   position: absolute;
  //   bottom: 2.5vh;
  //   left: 2.5vw;

  //   .mute-button {
  //     height: 12vh;
  //     cursor: pointer;
  //   }
  // }
}

// Animation Keyframes
@keyframes fly-half {
  0% {
    transform: translateX(-100vw);
  }

  100% {
    transform: translateX(-8vw);
  }
}

@keyframes fly-full {
  0% {
    transform: translateX(-100vw);
  }

  25% {
    transform: translateX(0vw);
  }

  75% {
    transform: translateX(0vw);
  }

  100% {
    transform: translateX(100vw);
  }
}

// Media Queries

@media (min-width: $screen-sm) {
}

@media (min-width: $screen-md) {
  .game {
    .banner-orange {
      min-width: 700px;
      font-size: 1.75em;
    }

    .next-exercise-screen {
      font-size: 2em;
    }

    .explanation {
      font-size: 2em;
    }

    .points {
      height: 90px;
      width: 300px;
      font-size: 45px;
    }
    .points-end {
      height: 90px;
      margin-left: -42px;
    }

    .lives {
      height: 90px;
      width: 320px;

      ul {
        li {
          .life {
            height: 75px;
          }
        }
      }
    }

    .game-end {
      font-size: 2em;
    }
  }
}

@media (min-width: $screen-lg) {
  .game {
    .banner-orange {
      min-width: 1000px;
      font-size: 2.5em;
    }

    .next-exercise-screen {
      font-size: 3em;
    }

    .explanation {
      font-size: 2.75em;
    }
  }
}
</style>
